// src/pages/Dashboard/Administrador/ActividadesPendientesDocentes.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Table, message, Typography, Button, Select, Input, Tabs, Tag, Space } from 'antd';
import { DownloadOutlined, SearchOutlined, FilterOutlined, ReloadOutlined } from '@ant-design/icons';
import apiClient from '../../../api/apiClient';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';
import { getCicloActual } from '../../../utils/cicloEscolar';
import { registrarDescargaExcel } from '../../../utils/bitacora';

const { Title, Text } = Typography;
const { Option } = Select;

const ActividadesPendientesDocentes = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('1');

  // Datos
  const [detalle, setDetalle] = useState([]);
  const [resumen, setResumen] = useState([]);
  const [loadingDetalle, setLoadingDetalle] = useState(false);
  const [loadingResumen, setLoadingResumen] = useState(false);

  // Filtros
  const [filtros, setFiltros] = useState({
    p_CicloEscolar: getCicloActual().toString(),
    IdGrado: null,
    IdSeccion: null,
    IdJornada: null,
    IdDocente: null,
  });

  // Catálogos
  const [catalogos, setCatalogos] = useState({
    grados: [],
    secciones: [],
    jornadas: [],
    docentes: [],
  });

  // Cargar catálogos
  useEffect(() => {
    const cargar = async () => {
      try {
        const [gradosRes, seccionesRes, jornadasRes, docentesRes] = await Promise.all([
          apiClient.get('/grados'),
          apiClient.get('/secciones'),
          apiClient.get('/jornadas'),
          apiClient.get('/docentes'),
        ]);

        setCatalogos({
          grados: Array.isArray(gradosRes.data.data || gradosRes.data) ? (gradosRes.data.data || gradosRes.data) : [],
          secciones: Array.isArray(seccionesRes.data.data || seccionesRes.data) ? (seccionesRes.data.data || seccionesRes.data) : [],
          jornadas: Array.isArray(jornadasRes.data.data || jornadasRes.data) ? (jornadasRes.data.data || jornadasRes.data) : [],
          docentes: docentesRes.data.success ? (docentesRes.data.data || []) : [],
        });
      } catch (err) {
        message.error('Error al cargar catálogos');
      }
    };
    cargar();
  }, []);

  // Construir params para API
  const buildParams = useCallback((includeDocente = true) => {
    const params = {};
    if (filtros.p_CicloEscolar) params.p_CicloEscolar = filtros.p_CicloEscolar;
    if (filtros.IdGrado) params.IdGrado = filtros.IdGrado;
    if (filtros.IdSeccion) params.IdSeccion = filtros.IdSeccion;
    if (filtros.IdJornada) params.IdJornada = filtros.IdJornada;
    if (includeDocente && filtros.IdDocente) params.IdDocente = filtros.IdDocente;
    return params;
  }, [filtros]);

  // Cargar detalle
  const cargarDetalle = useCallback(async () => {
    setLoadingDetalle(true);
    try {
      const res = await apiClient.get('/reportes/actividades-pendientes-docentes', { params: buildParams(true) });
      if (res.data.success) {
        setDetalle(res.data.data || []);
      } else {
        setDetalle([]);
      }
    } catch (error) {
      message.error('Error al cargar actividades pendientes');
      setDetalle([]);
    } finally {
      setLoadingDetalle(false);
    }
  }, [buildParams]);

  // Cargar resumen
  const cargarResumen = useCallback(async () => {
    setLoadingResumen(true);
    try {
      const res = await apiClient.get('/reportes/resumen-pendientes-docentes', { params: buildParams(false) });
      if (res.data.success) {
        setResumen(res.data.data || []);
      } else {
        setResumen([]);
      }
    } catch (error) {
      message.error('Error al cargar resumen de docentes');
      setResumen([]);
    } finally {
      setLoadingResumen(false);
    }
  }, [buildParams]);

  // Cargar datos según tab activo
  const handleBuscar = useCallback(() => {
    if (activeTab === '1') {
      cargarDetalle();
    } else {
      cargarResumen();
    }
  }, [activeTab, cargarDetalle, cargarResumen]);

  // Tag de color según días de atraso
  const renderDiasAtraso = (dias) => {
    if (dias == null) return '-';
    const num = Number(dias);
    let color = 'green';
    if (num >= 10) color = 'red';
    else if (num >= 5) color = 'orange';
    return <Tag color={color}>{num} días</Tag>;
  };

  // Columnas Tab 1: Detalle
  const columnasDetalle = [
    { title: '#', render: (_, __, i) => i + 1, width: 50, align: 'center' },
    { title: 'Docente', dataIndex: 'NombreDocente', width: 180 },
    { title: 'Curso', dataIndex: 'NombreCurso', width: 160 },
    { title: 'Grado', dataIndex: 'NombreGrado', width: 100 },
    { title: 'Sección', dataIndex: 'NombreSeccion', width: 90, align: 'center' },
    { title: 'Jornada', dataIndex: 'NombreJornada', width: 100 },
    { title: 'Actividad', dataIndex: 'NombreActividad', width: 160 },
    { title: 'Tipo', dataIndex: 'TipoActividad', width: 90, render: (t) => <Tag>{t}</Tag> },
    {
      title: 'Fecha Actividad', dataIndex: 'FechaActividad', width: 120,
      render: (f) => f ? new Date(f).toLocaleDateString('es-GT') : '-'
    },
    { title: 'Días Atraso', dataIndex: 'DiasAtrasados', width: 110, align: 'center', render: renderDiasAtraso },
    {
      title: 'Sin Calificar', dataIndex: 'AlumnosSinCalificar', width: 110, align: 'center',
      render: (v, r) => <Tag color="volcano">{v}/{r.TotalAlumnos}</Tag>
    },
  ];

  // Columnas Tab 2: Resumen
  const columnasResumen = [
    { title: '#', render: (_, __, i) => i + 1, width: 50, align: 'center' },
    { title: 'Docente', dataIndex: 'NombreDocente', width: 250 },
    {
      title: 'Actividades Pendientes', dataIndex: 'TotalActividadesPendientes', width: 160, align: 'center',
      render: (v) => <Tag color="volcano">{v}</Tag>,
      sorter: (a, b) => (a.TotalActividadesPendientes || 0) - (b.TotalActividadesPendientes || 0),
      defaultSortOrder: 'descend',
    },
    {
      title: 'Alumnos Sin Calificar', dataIndex: 'TotalAlumnosSinCalificar', width: 160, align: 'center',
      render: (v) => <Tag color="orange">{v}</Tag>,
    },
    { title: 'Actividad Más Atrasada', dataIndex: 'ActividadMasAtrasada', width: 200 },
    {
      title: 'Máx. Días Atraso', dataIndex: 'MaxDiasAtraso', width: 140, align: 'center',
      render: renderDiasAtraso,
      sorter: (a, b) => (a.MaxDiasAtraso || 0) - (b.MaxDiasAtraso || 0),
    },
  ];

  // Exportar Excel
  const exportarExcel = (datos, columnas, nombreReporte) => {
    if (!datos || datos.length === 0) {
      message.warning('No hay datos para exportar');
      return;
    }

    const headers = columnas.filter(c => c.dataIndex).map(c => c.title);
    const keys = columnas.filter(c => c.dataIndex).map(c => c.dataIndex);

    const filas = datos.map(row =>
      keys.reduce((obj, key, i) => {
        let val = row[key];
        if (key === 'FechaActividad' && val) {
          val = new Date(val).toLocaleDateString('es-GT');
        }
        obj[headers[i]] = val ?? '';
        return obj;
      }, {})
    );

    const ws = XLSX.utils.json_to_sheet(filas);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Reporte');
    const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([buf], { type: 'application/octet-stream' }), `${nombreReporte}.xlsx`);
    registrarDescargaExcel(nombreReporte);
    message.success('Excel descargado correctamente');
  };

  const tabItems = [
    {
      key: '1',
      label: `Detalle de Actividades (${detalle.length})`,
      children: (
        <>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
            <Button
              icon={<DownloadOutlined />}
              onClick={() => exportarExcel(detalle, columnasDetalle, 'Actividades_Pendientes_Detalle')}
              disabled={detalle.length === 0}
            >
              Exportar Excel
            </Button>
          </div>
          <Table
            columns={columnasDetalle}
            dataSource={detalle}
            rowKey={(r, i) => `detalle-${i}`}
            loading={loadingDetalle}
            pagination={{
              pageSize: 20,
              showSizeChanger: true,
              pageSizeOptions: ['10', '20', '50', '100'],
              showTotal: (total) => `Total: ${total} actividades pendientes`
            }}
            scroll={{ x: 1500 }}
            bordered
            size="small"
          />
        </>
      ),
    },
    {
      key: '2',
      label: `Resumen por Docente (${resumen.length})`,
      children: (
        <>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
            <Button
              icon={<DownloadOutlined />}
              onClick={() => exportarExcel(resumen, columnasResumen, 'Resumen_Pendientes_Docentes')}
              disabled={resumen.length === 0}
            >
              Exportar Excel
            </Button>
          </div>
          <Table
            columns={columnasResumen}
            dataSource={resumen}
            rowKey={(r, i) => `resumen-${i}`}
            loading={loadingResumen}
            pagination={{
              pageSize: 20,
              showSizeChanger: true,
              pageSizeOptions: ['10', '20', '50', '100'],
              showTotal: (total) => `Total: ${total} docentes`
            }}
            scroll={{ x: 1000 }}
            bordered
            size="small"
          />
        </>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Title level={2}>
        <FilterOutlined /> Actividades Pendientes de Docentes
      </Title>
      <Text type="secondary">Reporte de actividades sin calificar por docente</Text>

      <Card style={{ marginTop: 24 }}>
        {/* Filtros */}
        <div style={{ display: 'flex', gap: 12, marginBottom: 24, flexWrap: 'wrap', alignItems: 'center' }}>
          <div>
            <Text type="secondary" style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>Ciclo Escolar</Text>
            <Input
              value={filtros.p_CicloEscolar}
              onChange={(e) => setFiltros(prev => ({ ...prev, p_CicloEscolar: e.target.value }))}
              style={{ width: 100 }}
              placeholder="Año"
            />
          </div>

          <div>
            <Text type="secondary" style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>Grado</Text>
            <Select
              value={filtros.IdGrado}
              onChange={(v) => setFiltros(prev => ({ ...prev, IdGrado: v }))}
              style={{ width: 160 }}
              placeholder="Todos"
              allowClear
            >
              {catalogos.grados.filter(g => g.IdGrado != null).map(g => (
                <Option key={g.IdGrado} value={g.IdGrado}>{g.NombreGrado}</Option>
              ))}
            </Select>
          </div>

          <div>
            <Text type="secondary" style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>Sección</Text>
            <Select
              value={filtros.IdSeccion}
              onChange={(v) => setFiltros(prev => ({ ...prev, IdSeccion: v }))}
              style={{ width: 120 }}
              placeholder="Todas"
              allowClear
            >
              {catalogos.secciones.filter(s => s.IdSeccion != null).map(s => (
                <Option key={s.IdSeccion} value={s.IdSeccion}>{s.NombreSeccion}</Option>
              ))}
            </Select>
          </div>

          <div>
            <Text type="secondary" style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>Jornada</Text>
            <Select
              value={filtros.IdJornada}
              onChange={(v) => setFiltros(prev => ({ ...prev, IdJornada: v }))}
              style={{ width: 140 }}
              placeholder="Todas"
              allowClear
            >
              {catalogos.jornadas.filter(j => j.IdJornada != null).map(j => (
                <Option key={j.IdJornada} value={j.IdJornada}>{j.NombreJornada}</Option>
              ))}
            </Select>
          </div>

          {activeTab === '1' && (
            <div>
              <Text type="secondary" style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>Docente</Text>
              <Select
                value={filtros.IdDocente}
                onChange={(v) => setFiltros(prev => ({ ...prev, IdDocente: v }))}
                style={{ width: 200 }}
                placeholder="Todos"
                allowClear
                showSearch
                optionFilterProp="children"
              >
                {catalogos.docentes.filter(d => d.idDocente != null).map(d => (
                  <Option key={d.idDocente} value={d.idDocente}>{d.NombreDocente}</Option>
                ))}
              </Select>
            </div>
          )}

          <div style={{ display: 'flex', alignItems: 'flex-end', paddingTop: 18 }}>
            <Space>
              <Button type="primary" icon={<SearchOutlined />} onClick={handleBuscar}>
                Buscar
              </Button>
              <Button
                icon={<ReloadOutlined />}
                onClick={() => {
                  setFiltros({
                    p_CicloEscolar: getCicloActual().toString(),
                    IdGrado: null,
                    IdSeccion: null,
                    IdJornada: null,
                    IdDocente: null,
                  });
                }}
              >
                Limpiar
              </Button>
            </Space>
          </div>
        </div>

        <Tabs
          activeKey={activeTab}
          onChange={(key) => setActiveTab(key)}
          items={tabItems}
        />
      </Card>

      <div style={{ marginTop: 40, textAlign: 'center', paddingBottom: 24 }}>
        <Button size="large" onClick={() => navigate('/dashboard')} style={{ minWidth: 200 }}>
          Regresar al Dashboard
        </Button>
      </div>
    </div>
  );
};

export default ActividadesPendientesDocentes;
